import { groupBy } from "lodash";
import { useMemo } from "react";
import { MedSpaServiceMenuItemPublicInfoType } from "@/__generated__/schema.graphql.types";
import { CareForms } from "@/components/serviceFlow/client/careFormsSection";
import { buildClientTokenContext } from "@/contexts/clientAccessTokenContext";
import { useClientByTokenQuery } from "@/graphql/queries/clientByToken.graphql.types";
import useClientCareForms from "@/hooks/client/useClientCareForms";
import { ACTIVE_VISIT_STATUSES, VisitStatus } from "@/types";

const PHOTO_CONSENT_FORM_NAME = "photo";

export default function useClientFormsByToken(
  token: string,
  services?: MedSpaServiceMenuItemPublicInfoType[],
  shouldShowOnlyAppointmentSpecificForms = false
) {
  const {
    data,
    loading,
    error: queryError,
  } = useClientByTokenQuery({
    variables: {
      token: token as string,
    },
    context: buildClientTokenContext(token),
    skip: !token,
  });

  const error =
    data?.clientAccessTokenByPk === null
      ? "Client Access Token expired. Please refresh the page."
      : queryError;

  const {
    consentFormSignatures,
    formSubmissions,
    visits,
    id: clientId,
    firstName,
    medspa,
  } = data?.clientAccessTokenByPk?.client ?? {};

  const intakeForms = useMemo(
    () =>
      shouldShowOnlyAppointmentSpecificForms && formSubmissions
        ? formSubmissions.filter((formSubmission) => {
            if (!services) return false;
            if (formSubmission.form.allServices) return true;

            const offerings = services.flatMap(
              (service) => service.offeringsIds
            );

            return formSubmission.form.medspaServiceOfferings
              .map((offering) => offering.medspaserviceofferingId)
              .some((offeringId) => offerings.includes(offeringId));
          })
        : formSubmissions,
    [formSubmissions, services, shouldShowOnlyAppointmentSpecificForms]
  );

  // We want to always show the most recent photo consent form signature even after the appointment
  const mostRecentUnsignedPhotoConsentFormSignatures = useMemo(() => {
    const photoConsentFormSignatures = consentFormSignatures?.filter((form) => {
      return (
        form.consentForm.name.toLowerCase().includes(PHOTO_CONSENT_FORM_NAME) &&
        !form.signedAt
      );
    });

    const groupedConsentFormSignatures = groupBy(
      photoConsentFormSignatures,
      "consentForm.name"
    );

    return Object.values(groupedConsentFormSignatures).map((forms) => {
      return forms.sort((a, b) => {
        return new Date(b.created).getTime() - new Date(a.created).getTime();
      })[0];
    });
  }, [consentFormSignatures]);

  const consentForms = useMemo(() => {
    const filteredConsentForms = consentFormSignatures?.filter((form) => {
      if (!form.visit) return true;

      if (
        mostRecentUnsignedPhotoConsentFormSignatures.find(
          (signature) => signature.id === form.id
        )
      )
        return ACTIVE_VISIT_STATUSES.includes(form.visit.status as VisitStatus);

      return (
        form.visit.status === VisitStatus.SCHEDULED ||
        form.visit.status === VisitStatus.CONFIRMED
      );
    });

    return shouldShowOnlyAppointmentSpecificForms && filteredConsentForms
      ? filteredConsentForms.filter((form) => {
          if (!services) return false;

          return form.consentForm.serviceMenuItems
            .map((serviceMenuItem) => serviceMenuItem.serviceMenuItem.id)
            .some((serviceMenuItemId) =>
              services.map((service) => service.id).includes(serviceMenuItemId)
            );
        })
      : filteredConsentForms;
  }, [
    consentFormSignatures,
    mostRecentUnsignedPhotoConsentFormSignatures,
    services,
    shouldShowOnlyAppointmentSpecificForms,
  ]);

  const { precare, postcare } = useClientCareForms(visits);

  const isAllConsentFormsSigned = useMemo(
    () => consentForms?.every((form) => form.signedAt),
    [consentForms]
  );
  const isAllIntakeFormsSubmitted = useMemo(
    () => intakeForms?.every((form) => form.submittedAt),
    [intakeForms]
  );

  const hasConsentForms = consentForms?.length > 0;
  const hasIntakeForms = intakeForms?.length > 0;

  const isAllFormsSubmitted = useMemo(() => {
    if (hasConsentForms && hasIntakeForms) {
      return isAllConsentFormsSigned && isAllIntakeFormsSubmitted;
    }
    if (hasConsentForms) {
      return isAllConsentFormsSigned;
    }
    if (hasIntakeForms) {
      return isAllIntakeFormsSubmitted;
    }
    return true;
  }, [
    hasIntakeForms,
    hasConsentForms,
    isAllConsentFormsSigned,
    isAllIntakeFormsSubmitted,
  ]);

  return useMemo(
    () => ({
      loading: !error && (loading || data === undefined),
      error,
      visits,
      hasForms: hasConsentForms || hasIntakeForms,
      forms: {
        consentForms,
        intakeForms,
        precare: filterCareForms(precare, services),
        postcare: filterCareForms(postcare, services),
      },
      isAllFormsSubmitted,
      clientId,
      firstName,
      medspa,
    }),
    [
      error,
      loading,
      data,
      visits,
      hasConsentForms,
      hasIntakeForms,
      consentForms,
      intakeForms,
      precare,
      services,
      postcare,
      isAllFormsSubmitted,
      clientId,
      firstName,
      medspa,
    ]
  );
}

const filterCareForms = (
  forms: CareForms,
  services?: MedSpaServiceMenuItemPublicInfoType[]
) => {
  if (!services) return forms;
  const serviceIds = services?.map((service) => service.id);

  return forms?.reduce((acc, form) => {
    const commonServices = form.services.filter((service) =>
      serviceIds.includes(service.id)
    );

    if (commonServices.length > 0) {
      acc.push({ ...form, appointmentServices: commonServices });
    }

    return acc;
  }, []);
};
